// How a saved card reads anywhere in this app.
//
// Before this module every screen formatted its own string, which is why the
// same card rendered as "Visa ****4242" in Profile > Payment and
// "Visa •••• 4242" in Account > Payment methods, and why neither of them knew
// that Stripe's brand code for American Express is `amex`.
//
// Mirrors blhClinicApp/src/components/shared/cards/cardLabel.js. The two apps
// share no package, so this is a deliberate fork with a named origin: a fix
// there transplants here verbatim, including the raw-Stripe branch of
// `normalizeCard`, which this app does not currently need.
//
// It lives in `ui/` rather than in a feature folder because four different
// screens render a card (Methods, PayDrawer, Credits, Profile > Payment), and
// it types its input structurally so nothing in `ui/` has to import a
// feature's types.

import { Text, TextProps } from "@chakra-ui/react";

const MASK = "••••";

/** Whatever shape the card arrived in. */
export type CardLike = {
  id?: string | null;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  isDefault?: boolean;
  /** The PATIENT's own label. The staff label never reaches this app. */
  nickname?: string | null;
  /** Only the raw Stripe object carries this; kept for parity with the clinic. */
  card?: {
    brand?: string | null;
    last4?: string | null;
    exp_month?: number | null;
    exp_year?: number | null;
  } | null;
  metadata?: { nickname?: string | null } | null;
  default?: boolean;
};

type NormalizedCard = {
  id: string | null;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
  nickname: string | null;
};

/** "Visa" / "Mastercard" / "Amex" — never the raw lowercase Stripe code. */
export function brandLabel(brand: string | null | undefined): string {
  if (!brand) return "Card";
  switch (String(brand).toLowerCase()) {
    case "visa":
      return "Visa";
    case "mastercard":
      return "Mastercard";
    case "amex":
    case "american_express":
      return "Amex";
    case "discover":
      return "Discover";
    case "diners":
      return "Diners Club";
    case "jcb":
      return "JCB";
    case "unionpay":
      return "UnionPay";
    case "eftpos_au":
      return "Eftpos";
    // Stripe's literal value when it cannot identify the card. "Unknown"
    // reads worse than the generic fallback, so it collapses to the same
    // word an absent brand gets.
    case "unknown":
      return "Card";
    default:
      return brand.charAt(0).toUpperCase() + brand.slice(1);
  }
}

/**
 * Flatten whichever shape arrived into one. `card.card` only exists on the raw
 * Stripe object, so its presence is what tells the two apart.
 */
export function normalizeCard(
  input: CardLike | null | undefined
): NormalizedCard | null {
  if (!input) return null;
  const raw = input.card && typeof input.card === "object" ? input.card : null;
  return {
    id: input.id || null,
    brand: (raw ? raw.brand : input.brand) || null,
    last4: (raw ? raw.last4 : input.last4) || null,
    expMonth: (raw ? raw.exp_month : input.expMonth) ?? null,
    expYear: (raw ? raw.exp_year : input.expYear) ?? null,
    // Both flags mean the same thing and both ship today.
    isDefault: Boolean(input.isDefault ?? input.default),
    nickname: input.nickname || input.metadata?.nickname || null,
  };
}

/** The card itself: "Visa •••• 4242". Never the nickname. */
export function cardDescriptor(input: CardLike | null | undefined): string {
  const c = normalizeCard(input);
  return `${brandLabel(c?.brand)} ${MASK} ${c?.last4 || "????"}`;
}

/** "10/2030", or "" when Stripe gave us no expiry. */
export function cardExpiry(input: CardLike | null | undefined): string {
  const c = normalizeCard(input);
  if (!c?.expMonth || !c?.expYear) return "";
  return `${String(c.expMonth).padStart(2, "0")}/${c.expYear}`;
}

export function cardNickname(input: CardLike | null | undefined): string {
  const value = normalizeCard(input)?.nickname;
  return typeof value === "string" ? value.trim() : "";
}

/**
 * How the card should be named to a person: the name they gave it when they
 * gave one, otherwise the card itself. Use this anywhere only one line is
 * available.
 */
export function cardLabel(input: CardLike | null | undefined): string {
  return cardNickname(input) || cardDescriptor(input);
}

/**
 * The line that belongs UNDER `cardLabel`. Empty when the label is already the
 * descriptor, so callers never render "Visa •••• 4242" twice.
 */
export function cardSubLabel(input: CardLike | null | undefined): string {
  const expiry = cardExpiry(input);
  const parts = cardNickname(input) ? [cardDescriptor(input)] : [];
  if (expiry) parts.push(`Expires ${expiry}`);
  return parts.join(" · ");
}

export const CardLabel = ({
  card,
  ...rest
}: { card: CardLike | null | undefined } & TextProps) => (
  <Text as="span" {...rest}>
    {cardLabel(card)}
  </Text>
);
